import React, {useEffect, useRef, useState} from 'react';

import {
	StyledAccordion,
	StyledAccordionContainer,
	StyledAccordionContent,
	StyledAccordionSection,
} from './styled';
import {Layout, Text} from '@phonectas/unifon-ui-kit';
import PlusAndMinusIcon from '../icons/PlusAndMinusIcon';
import ChevronUpAndDownIcon from '../icons/ChevronUpAndDownIcon';

interface AccordionProps {
    title: string;
    children: React.ReactNode;
    expanded?: boolean;
    onToggle?: () => void;
    allowMultiple?: boolean;
    bigIcon?: boolean;
	iconType?: 'plus-minus' | 'chevron-up-down';
}

export const Accordion: React.FC<AccordionProps> = ({
	title,
	children,
	expanded: controlledExpanded,
	onToggle,
	bigIcon = false,
	iconType = 'plus-minus',
}) => {
	const [active, setActive] = useState<boolean>(!!controlledExpanded);
	const contentRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState<string>('0px');

	// If controlled, update active state from prop
	useEffect(() => {
		if (typeof controlledExpanded === 'boolean') {
			setActive(controlledExpanded);
		}
	}, [controlledExpanded]);

	useEffect(() => {
		if (active && contentRef.current) {
			setHeight(`${contentRef.current.scrollHeight + 20}px`);
		} else {
			setHeight('0px');
		}
	}, [active, children]);

	function toggleAccordion() {
		if (onToggle) {
			onToggle();
		} else {
			setActive((prev) => !prev);
		}
	}

	return (
		<StyledAccordionContainer>
			<StyledAccordionSection>
				<StyledAccordion
					onClick={toggleAccordion}
					$active={active}
				>
					<Layout fullWidth alignItems="center" justifyContent="space-between">
						<span>
							<Text variant="h5">{title}</Text>
						</span>
						<span
							style={{
								display: 'flex',
								alignItems: 'center',
								height: bigIcon ? '24px' : '18px',
								width: bigIcon ? '24px' : '18px',
							}}
						>
							{
								iconType === 'chevron-up-down' ? (
									<ChevronUpAndDownIcon size={bigIcon ? 24 : 18} isUp={active}/>
								) : (
									<PlusAndMinusIcon $expanded={active} />
								)
							}
						</span>
					</Layout>
				</StyledAccordion>
				<StyledAccordionContent
					$expanded={active}
					ref={contentRef}
					style={{maxHeight: height}}
				>
					{children}
				</StyledAccordionContent>
			</StyledAccordionSection>
		</StyledAccordionContainer>
	);
};

export default Accordion;